import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Building2,
  Plus,
  Edit2,
  Trash2,
  User,
  Mail,
  Phone,
  MapPin,
  FileText
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const SupplierForm = ({ supplier, onSuccess, onClose }) => {
  const [formData, setFormData] = useState({
    name: supplier?.name || "",
    contact_person: supplier?.contact_person || "",
    email: supplier?.email || "",
    phone: supplier?.phone || "",
    address: supplier?.address || "",
    notes: supplier?.notes || ""
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Bitte Name eingeben");
      return;
    }

    setSaving(true);
    try {
      if (supplier) {
        await axios.put(`${API}/suppliers/${supplier.id}`, formData);
        toast.success("Lieferant aktualisiert");
      } else {
        await axios.post(`${API}/suppliers`, formData);
        toast.success("Lieferant erstellt");
      }
      onClose();
      onSuccess();
    } catch (err) {
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>Firmenname *</Label>
        <Input
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="z.B. Metro Cash & Carry"
          data-testid="supplier-name-input"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Ansprechpartner</Label>
          <Input
            value={formData.contact_person}
            onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
            placeholder="Max Mustermann"
            data-testid="supplier-contact-input"
          />
        </div>
        <div className="space-y-2">
          <Label>Telefon</Label>
          <Input
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            placeholder="+49 123 456789"
            data-testid="supplier-phone-input"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>E-Mail</Label>
        <Input
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          placeholder="kontakt@lieferant.de"
          data-testid="supplier-email-input"
        />
      </div>

      <div className="space-y-2">
        <Label>Adresse</Label>
        <Textarea
          value={formData.address}
          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
          placeholder="Straße, PLZ Ort"
          rows={2}
          data-testid="supplier-address-input"
        />
      </div>

      <div className="space-y-2">
        <Label>Notizen</Label>
        <Textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Öffnungszeiten, Besonderheiten..."
          rows={2}
          data-testid="supplier-notes-input"
        />
      </div>

      <div className="flex gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onClose} className="flex-1">
          Abbrechen
        </Button>
        <Button 
          type="submit" 
          disabled={saving}
          className="flex-1 bg-rose-600 hover:bg-rose-700"
          data-testid="supplier-save-btn"
        >
          {saving ? "Speichern..." : supplier ? "Aktualisieren" : "Erstellen"}
        </Button>
      </div>
    </form>
  );
};

const SupplierCard = ({ supplier, onEdit, onDelete }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card className="border-slate-200 hover:border-slate-300 transition-colors">
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 bg-slate-100 rounded-md flex items-center justify-center">
                <Building2 className="w-6 h-6 text-slate-600" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-lg">{supplier.name}</h3>
                {supplier.contact_person && (
                  <div className="flex items-center gap-2 text-sm text-slate-500 mt-1">
                    <User className="w-3 h-3" />
                    {supplier.contact_person}
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-1">
              <Button
                size="icon"
                variant="ghost"
                onClick={() => onEdit(supplier)}
                data-testid={`edit-supplier-${supplier.id}`}
              >
                <Edit2 className="w-4 h-4 text-slate-400" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => onDelete(supplier)}
                data-testid={`delete-supplier-${supplier.id}`}
              >
                <Trash2 className="w-4 h-4 text-slate-400 hover:text-rose-600" />
              </Button>
            </div>
          </div>

          <div className="mt-4 space-y-2">
            {supplier.email && (
              <div className="flex items-center gap-2 text-sm">
                <Mail className="w-4 h-4 text-slate-400" />
                <a href={`mailto:${supplier.email}`} className="text-blue-600 hover:underline">
                  {supplier.email}
                </a>
              </div>
            )}
            {supplier.phone && (
              <div className="flex items-center gap-2 text-sm">
                <Phone className="w-4 h-4 text-slate-400" />
                <a href={`tel:${supplier.phone}`} className="text-blue-600 hover:underline">
                  {supplier.phone}
                </a>
              </div>
            )}
            {supplier.address && (
              <div className="flex items-start gap-2 text-sm text-slate-600">
                <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
                <span className="whitespace-pre-line">{supplier.address}</span>
              </div>
            )}
            {supplier.notes && (
              <div className="flex items-start gap-2 text-sm text-slate-500 mt-2 pt-2 border-t">
                <FileText className="w-4 h-4 text-slate-400 mt-0.5" />
                <span className="whitespace-pre-line">{supplier.notes}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

const SuppliersPage = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editSupplier, setEditSupplier] = useState(null);
  const [deleteSupplier, setDeleteSupplier] = useState(null);

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const res = await axios.get(`${API}/suppliers`);
      setSuppliers(res.data);
    } catch (err) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (supplier) => {
    setEditSupplier(supplier);
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteSupplier) return;
    try {
      await axios.delete(`${API}/suppliers/${deleteSupplier.id}`);
      toast.success("Lieferant gelöscht");
      fetchSuppliers();
    } catch (err) {
      toast.error("Fehler beim Löschen");
    } finally {
      setDeleteSupplier(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight">
            Lieferanten
          </h1>
          <p className="text-slate-500 mt-1">
            Lieferanten-Stammdaten verwalten
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditSupplier(null);
        }}>
          <DialogTrigger asChild>
            <Button 
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="add-supplier-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Neuer Lieferant
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editSupplier ? "Lieferant bearbeiten" : "Neuer Lieferant"}
              </DialogTitle>
            </DialogHeader>
            <SupplierForm
              supplier={editSupplier}
              onSuccess={fetchSuppliers}
              onClose={() => {
                setDialogOpen(false);
                setEditSupplier(null);
              }}
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Supplier List */}
      {suppliers.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {suppliers.map((supplier) => (
            <SupplierCard
              key={supplier.id}
              supplier={supplier}
              onEdit={handleEdit}
              onDelete={setDeleteSupplier}
            />
          ))}
        </div>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <Building2 className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">Noch keine Lieferanten angelegt</p>
            <p className="text-sm text-slate-400 mt-1">
              Klicken Sie auf "Neuer Lieferant" um zu starten
            </p>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteSupplier} onOpenChange={() => setDeleteSupplier(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Lieferant löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie "{deleteSupplier?.name}" wirklich löschen?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-rose-600 hover:bg-rose-700"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SuppliersPage;
